import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Users, Clock, AlertTriangle, Shield } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface GameRoster {
  id: string;
  player_id: string;
  team_side: 'home' | 'away';
  is_eligible: boolean;
  eligibility_notes?: string;
  added_at: string;
  player_profile?: {
    first_name: string;
    last_name: string;
    profile_photo_url?: string;
    username?: string;
  };
}

interface GameRosterManagerProps {
  gameId: string;
  homeTeamName: string;
  awayTeamName: string;
  gameDate: string;
  gameTime: string;
  isVenueOwner?: boolean;
}

export const GameRosterManager: React.FC<GameRosterManagerProps> = ({
  gameId,
  homeTeamName,
  awayTeamName,
  gameDate,
  gameTime,
  isVenueOwner = false
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [rosters, setRosters] = useState<GameRoster[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGameRosters();
  }, [gameId]);

  const loadGameRosters = async () => {
    try {
      setLoading(true);
      
      // Get venue game info to find team IDs
      const { data: gameData, error: gameError } = await supabase
        .from('venue_games')
        .select('home_team_id, away_team_id')
        .eq('id', gameId)
        .single();

      if (gameError) throw gameError;

      // Fetch game rosters
      const { data, error } = await supabase
        .from('game_rosters')
        .select(`
          id,
          player_id,
          team_side,
          is_eligible,
          eligibility_notes,
          added_at
        `)
        .eq('game_id', gameId);

      if (error) throw error;

      // Fetch player profiles separately
      const rosters = data || [];
      const rostersWithProfiles = await Promise.all(
        rosters.map(async (roster) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name, profile_photo_url, username')
            .eq('id', roster.player_id)
            .single();

          return {
            ...roster,
            player_profile: profile
          } as GameRoster;
        })
      );

      // Add primary coaches (team creators) to the roster display
      const primaryCoaches = [];
      
      if (gameData.home_team_id) {
        const { data: homeTeam } = await supabase
          .from('teams')
          .select('created_by')
          .eq('team_id', gameData.home_team_id)
          .single();

        if (homeTeam?.created_by) {
          const { data: coachProfile } = await supabase
            .from('profiles')
            .select('first_name, last_name, profile_photo_url, username')
            .eq('id', homeTeam.created_by)
            .single();

          if (coachProfile) {
            primaryCoaches.push({
              id: `primary-home-${homeTeam.created_by}`,
              player_id: homeTeam.created_by,
              team_side: 'home' as const,
              is_eligible: true,
              eligibility_notes: null,
              added_at: new Date().toISOString(),
              player_profile: {
                ...coachProfile,
                first_name: `${coachProfile.first_name} (Primary Coach)`,
              }
            });
          }
        }
      }

      if (gameData.away_team_id) {
        const { data: awayTeam } = await supabase
          .from('teams')
          .select('created_by')
          .eq('team_id', gameData.away_team_id)
          .single();

        if (awayTeam?.created_by) {
          const { data: coachProfile } = await supabase
            .from('profiles')
            .select('first_name, last_name, profile_photo_url, username')
            .eq('id', awayTeam.created_by)
            .single();

          if (coachProfile) {
            primaryCoaches.push({
              id: `primary-away-${awayTeam.created_by}`,
              player_id: awayTeam.created_by,
              team_side: 'away' as const,
              is_eligible: true,
              eligibility_notes: null,
              added_at: new Date().toISOString(),
              player_profile: {
                ...coachProfile,
                first_name: `${coachProfile.first_name} (Primary Coach)`,
              }
            });
          }
        }
      }

      // Combine rosters with primary coaches, avoiding duplicates
      const allRosters = [...rostersWithProfiles];
      primaryCoaches.forEach(coach => {
        const exists = allRosters.some(r => r.player_id === coach.player_id && r.team_side === coach.team_side);
        if (!exists) {
          allRosters.push(coach);
        }
      });

      setRosters(allRosters);
    } catch (error) {
      console.error('Error loading rosters:', error);
      toast({
        title: "Error",
        description: "Failed to load game rosters",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const markPlayerIneligible = async (rosterId: string, reason: string) => {
    try {
      const { error } = await supabase
        .from('game_rosters')
        .update({ 
          is_eligible: false, 
          eligibility_notes: reason 
        })
        .eq('id', rosterId);

      if (error) throw error;

      // Create violation record
      const roster = rosters.find(r => r.id === rosterId);
      if (roster) {
        await supabase
          .from('player_eligibility_violations')
          .insert({
            player_id: roster.player_id,
            game_id: gameId,
            violation_type: 'ineligible_to_play',
            violation_reason: reason,
            reported_by: user?.id,
            venue_id: user?.id
          });
      }

      await loadGameRosters();
      toast({
        title: "Player marked ineligible",
        description: "Violation has been recorded"
      });
    } catch (error) {
      console.error('Error marking player ineligible:', error);
      toast({
        title: "Error",
        description: "Failed to update player eligibility",
        variant: "destructive"
      });
    }
  };

  const getPlayerInitials = (firstName?: string, lastName?: string, username?: string) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (username) {
      return username.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  const getPlayerDisplayName = (player: GameRoster['player_profile']) => {
    if (player?.first_name && player?.last_name) {
      return `${player.first_name} ${player.last_name}`;
    }
    return player?.username || 'Unknown Player';
  };

  const homeTeamRoster = rosters.filter(r => r.team_side === 'home');
  const awayTeamRoster = rosters.filter(r => r.team_side === 'away');

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Game Roster: {homeTeamName} vs {awayTeamName}
          </CardTitle>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            {gameDate} at {gameTime}
          </div>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Home Team */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {homeTeamName} (Home)
              <Badge variant="secondary" className="ml-2">
                {homeTeamRoster.length} players
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {homeTeamRoster.map((roster) => (
              <div
                key={roster.id}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={roster.player_profile?.profile_photo_url} />
                    <AvatarFallback>
                      {getPlayerInitials(
                        roster.player_profile?.first_name,
                        roster.player_profile?.last_name,
                        roster.player_profile?.username
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">
                      {getPlayerDisplayName(roster.player_profile)}
                    </p>
                    {!roster.is_eligible && (
                      <div className="flex items-center gap-1 text-red-500 text-sm">
                        <AlertTriangle className="h-3 w-3" />
                        Ineligible: {roster.eligibility_notes}
                      </div>
                    )}
                  </div>
                </div>
                {isVenueOwner && roster.is_eligible && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => markPlayerIneligible(roster.id, 'Rule violation')}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Mark Ineligible
                  </Button>
                )}
              </div>
            ))}
            {homeTeamRoster.length === 0 && (
              <p className="text-muted-foreground text-center py-4">
                No players added yet
              </p>
            )}
          </CardContent>
        </Card>

        {/* Away Team */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {awayTeamName} (Away)
              <Badge variant="secondary" className="ml-2">
                {awayTeamRoster.length} players
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {awayTeamRoster.map((roster) => (
              <div
                key={roster.id}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={roster.player_profile?.profile_photo_url} />
                    <AvatarFallback>
                      {getPlayerInitials(
                        roster.player_profile?.first_name,
                        roster.player_profile?.last_name,
                        roster.player_profile?.username
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">
                      {getPlayerDisplayName(roster.player_profile)}
                    </p>
                    {!roster.is_eligible && (
                      <div className="flex items-center gap-1 text-red-500 text-sm">
                        <AlertTriangle className="h-3 w-3" />
                        Ineligible: {roster.eligibility_notes}
                      </div>
                    )}
                  </div>
                </div>
                {isVenueOwner && roster.is_eligible && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => markPlayerIneligible(roster.id, 'Rule violation')}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Mark Ineligible
                  </Button>
                )}
              </div>
            ))}
            {awayTeamRoster.length === 0 && (
              <p className="text-muted-foreground text-center py-4">
                No players added yet
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};